import {
  Avatar,
  AvatarGroup,
  Box,
  Divider,
  HStack,
  IconButton,
  Spacer,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { format } from 'date-fns';

import { SFSquareArrowUp, SFTrashIcon, SFListIcon } from '@assets/icons';
import { pluralized } from '@util/helpers';
import { useMutationToast } from '@util/hooks';
import { useUserStore } from '@store/app.store';
import {
  useArchiveInitiative,
  useInitiativeById
} from '@services/query-services/initiatives.query-service';
import ReskinCardHeader from '@components/general/card/reskin-card-structure/reskin-card-header.component';
import DeleteConfirmationModal from '@components/general/modals/delete-confirmation-modal.component';
import ShareCollectionModal from '@components/collection/share-collection-modal.component';
import SummaryCardSkeleton from '../summary-card/summary-card-skeleton.component';

const CollectionCard = ({ collectionId }) => {
  const user = useUserStore((state) => state.user);
  const { data: collection, isLoading } = useInitiativeById(collectionId, {
    enabled: !!collectionId
  });
  const isUserCreator = user?.id === collection?.creator?.id;
  const link = `/collection/${collectionId}`;
  const deleteConfirmationDisclosure = useDisclosure();
  const shareCollectionDisclosure = useDisclosure();

  const { mutateAsync: archiveCollection, isLoading: isArchivingCollection } =
    useArchiveInitiative();
  const { onSuccess: onDeleteSuccess, onError: onDeleteError } = useMutationToast(
    'collection',
    'delete'
  );

  const onCollectionDelete = async () => {
    await archiveCollection(
      { id: collectionId, value: true },
      { onSuccess: onDeleteSuccess, onError: onDeleteError }
    );
    deleteConfirmationDisclosure.onClose();
  };

  if (isLoading) {
    return <SummaryCardSkeleton />;
  }

  return (
    <Stack
      layerStyle='section-card'
      borderRadius='xl'
      data-testid='reskin-summary-card'
      gap={0}
    >
      <ReskinCardHeader
        title={
          <Text
            variant='title-md'
            lineHeight='28px'
          >
            {collection?.title}
          </Text>
        }
        titleLink={link}
        leftElement={
          <SFListIcon
            color='brandPrimary.onContainer'
            mr={1}
          />
        }
      >
        <HStack
          color='surface.onSurfaceVariant'
          gap={3}
          mt={1}
        >
          <Text variant='label-reg-lg'>{pluralized('item', collection?.num_of_cards ?? 0)}</Text>
          <Text variant='label-reg-lg'>{format(collection?.updated_at ?? 0, 'MMM d, yyyy')}</Text>
        </HStack>
      </ReskinCardHeader>
      <Divider style={{ margin: 0 }} />
      <Box
        as={ReactRouterLink}
        to={link}
        p={3}
        pb={0}
      >
        <Text
          variant='body-lighter-md'
          as='span'
          noOfLines={3}
        >
          {collection?.description}
        </Text>
      </Box>
      <Spacer />
      <HStack p={3}>
        <AvatarGroup
          size='xsm'
          max={2}
        >
          {collection?.collaborators?.map((p) => (
            <Avatar
              src={p?.avatar_url || ''}
              key={`${p?.email || p?.name}-avatar`}
              name={p?.name}
            />
          ))}
        </AvatarGroup>
        <Spacer />
        <HStack gap={1}>
          {isUserCreator && (
            <IconButton
              data-testid='share-collection-button'
              onClick={shareCollectionDisclosure.onOpen}
              isRound
              variant='reskinGhost'
              colorScheme='surface'
              size='xs'
              icon={<SFSquareArrowUp boxSize={5} />}
            />
          )}
          <IconButton
            data-testid='delete-collection-button'
            onClick={deleteConfirmationDisclosure.onOpen}
            isRound
            variant='reskinGhost'
            colorScheme='surface'
            size='xs'
            icon={<SFTrashIcon boxSize={4} />}
          />
        </HStack>
      </HStack>
      <DeleteConfirmationModal
        disclosure={deleteConfirmationDisclosure}
        headerText='Delete Collection Permanently?'
        onConfirmDelete={onCollectionDelete}
        isDeleting={isArchivingCollection}
      >
        <Text>Are you sure you want to delete this collection?</Text>
      </DeleteConfirmationModal>
      {isUserCreator && (
        <ShareCollectionModal
          collection={collection}
          disclosure={shareCollectionDisclosure}
        />
      )}
    </Stack>
  );
};

export default CollectionCard;
